import { useState, useRef, useCallback } from 'react';
import { processUpload, ProcessUploadOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Sparkles, Loader2, CheckCircle, X, FileText, Upload, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

type Category = { id: string; name: string };
type ContentType = 'SOP' | 'Training Module' | 'Induction Section';

interface FileItem {
  id: string;
  file: File;
  title: string;
  contentType: ContentType;
  categoryId: string;
  department: string;
  tags: string[];
  generateQuiz: boolean;
  status: 'pending' | 'processing' | 'done' | 'error';
  error?: string;
  result?: ProcessUploadOutputType;
}

const TAGS = ['Service & Floor', 'Cellar & Beer', 'Safety & Hygiene', 'Admin & Finance', 'Guest Management', 'Training', 'Equipment', 'Licensing'];

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => { resolve((reader.result as string).split(',')[1]); };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function FileCard({ item, categories, onUpdate, onRemove }: {
  item: FileItem;
  categories: Category[];
  onUpdate: (id: string, updates: Partial<FileItem>) => void;
  onRemove: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const isDone = item.status === 'done';
  const isError = item.status === 'error';
  const isProcessing = item.status === 'processing';

  return (
    <div className={`border rounded-lg overflow-hidden transition-colors ${isDone ? 'border-green-500/40 bg-green-500/5' : isError ? 'border-destructive/40 bg-destructive/5' : isProcessing ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'}`}>
      <div className="flex items-center gap-3 p-3">
        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground truncate">{item.file.name}</p>
          <p className="text-xs text-muted-foreground/60">{(item.file.size / 1024 / 1024).toFixed(1)} MB</p>
        </div>
        {isProcessing && <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />}
        {isDone && <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />}
        {isError && <AlertCircle className="w-4 h-4 text-destructive shrink-0" />}
        {item.status === 'pending' && (
          <button onClick={() => setExpanded(e => !e)} className="text-muted-foreground hover:text-foreground p-0.5">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
        {item.status === 'pending' && (
          <button onClick={() => onRemove(item.id)} className="text-muted-foreground hover:text-destructive p-0.5 ml-1">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {item.status === 'pending' && expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-border pt-3">
          <Input
            value={item.title}
            onChange={e => onUpdate(item.id, { title: e.target.value })}
            placeholder="Document title (e.g. Bar Opening Procedure)"
            className="h-8 text-sm"
          />
          <div className="grid grid-cols-3 gap-2">
            <Select value={item.contentType} onValueChange={v => onUpdate(item.id, { contentType: v as ContentType })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="SOP">SOP</SelectItem>
                <SelectItem value="Training Module">Training Module</SelectItem>
                <SelectItem value="Induction Section">Induction</SelectItem>
              </SelectContent>
            </Select>
            <Select value={item.categoryId || 'none'} onValueChange={v => onUpdate(item.id, { categoryId: v === 'none' ? '' : v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No category</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={item.department || 'none'} onValueChange={v => onUpdate(item.id, { department: v === 'none' ? '' : v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Dept." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">All depts</SelectItem>
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="Front of House">Front of House</SelectItem>
                <SelectItem value="Back of House">Back of House</SelectItem>
                <SelectItem value="Kitchen">Kitchen</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-1">
            {TAGS.map(tag => (
              <button key={tag} onClick={() => onUpdate(item.id, { tags: item.tags.includes(tag) ? item.tags.filter(t => t !== tag) : [...item.tags, tag] })}
                className={`text-xs px-2 py-0.5 border rounded transition-colors ${item.tags.includes(tag) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                {tag}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id={`quiz-${item.id}`} checked={item.generateQuiz} onChange={e => onUpdate(item.id, { generateQuiz: e.target.checked })} className="accent-primary" />
            <label htmlFor={`quiz-${item.id}`} className="text-xs text-muted-foreground">Generate 8 quiz questions</label>
          </div>
        </div>
      )}

      {isDone && item.result && (
        <div className="px-3 pb-3 border-t border-green-500/20 pt-2 text-xs text-muted-foreground">
          <p className="text-foreground font-medium">{item.result.enhancedTitle}</p>
          <p className="mt-0.5">{item.result.questionsCreated > 0 ? `${item.result.questionsCreated} quiz questions created` : 'No quiz questions'} · Saved as Draft</p>
        </div>
      )}

      {isError && item.error && (
        <div className="px-3 pb-3 border-t border-destructive/20 pt-2 text-xs text-destructive">
          {item.error}
        </div>
      )}
    </div>
  );
}

interface Props {
  categories: Category[];
  onSuccess: () => void;
}

export default function PdfUploadTab({ categories, onSuccess }: Props) {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [processing, setProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const arr = Array.from(newFiles).filter(f => f.type === 'application/pdf');
    const oversized = arr.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length > 0) {
      toast.error(`${oversized.length} file(s) skipped — max 10MB per PDF`);
    }
    const valid = arr.filter(f => f.size <= 10 * 1024 * 1024);
    if (valid.length === 0) return;
    const items: FileItem[] = valid.map(file => ({
      id: Math.random().toString(36).slice(2),
      file,
      title: file.name.replace(/\.pdf$/i, '').replace(/[-_]/g, ' '),
      contentType: 'SOP',
      categoryId: '',
      department: '',
      tags: [],
      generateQuiz: true,
      status: 'pending',
    }));
    setFiles(prev => [...prev, ...items]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const updateFile = (id: string, updates: Partial<FileItem>) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const processAll = async () => {
    const pending = files.filter(f => f.status === 'pending');
    if (pending.length === 0) return;
    if (pending.some(f => !f.title.trim())) {
      toast.error('Please add a title for all documents');
      return;
    }

    setProcessing(true);
    let successCount = 0;

    for (const item of pending) {
      setFiles(prev => prev.map(f => f.id === item.id ? { ...f, status: 'processing' } : f));
      try {
        const pdfBase64 = await readFileAsBase64(item.file);
        const result = await processUpload({
          title: item.title,
          pdfBase64,
          pdfFilename: item.file.name,
          contentType: item.contentType,
          categoryId: item.categoryId || undefined,
          department: item.department || undefined,
          tags: item.tags,
          generateQuiz: item.generateQuiz,
        });
        setFiles(prev => prev.map(f => f.id === item.id ? { ...f, status: 'done', result } : f));
        successCount++;
      } catch (e: any) {
        setFiles(prev => prev.map(f => f.id === item.id ? { ...f, status: 'error', error: e.message || 'Processing failed' } : f));
      }
    }

    setProcessing(false);
    if (successCount > 0) {
      toast.success(`✓ ${successCount} document${successCount > 1 ? 's' : ''} processed successfully`);
      onSuccess();
    }
  };

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const doneCount = files.filter(f => f.status === 'done').length;

  return (
    <div className="space-y-4">
      <div className="border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground/80 rounded-lg">
        <p className="font-medium text-foreground mb-1">📄 PDF Upload</p>
        <p>Upload up to 10 PDFs at once. AI will extract the text, structure it, add it to the knowledge bank, and generate quiz questions. Max 10MB per file. PDFs must contain selectable text (not scanned images).</p>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragging ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}
      >
        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">Drop PDF files here or click to browse</p>
        <p className="text-xs text-muted-foreground mt-1">Multiple files supported · PDF only · Max 10MB each</p>
        <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" multiple className="hidden" onChange={e => e.target.files && addFiles(e.target.files)} />
      </div>

      {/* File Queue */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{files.length} file{files.length > 1 ? 's' : ''} queued {doneCount > 0 ? `· ${doneCount} done` : ''}</p>
            {doneCount > 0 && (
              <button onClick={() => setFiles(prev => prev.filter(f => f.status !== 'done'))} className="text-xs text-muted-foreground hover:text-foreground">
                Clear completed
              </button>
            )}
          </div>
          {files.map(item => (
            <FileCard key={item.id} item={item} categories={categories} onUpdate={updateFile} onRemove={removeFile} />
          ))}
        </div>
      )}

      {pendingCount > 0 && (
        <Button onClick={processAll} disabled={processing} className="w-full" size="lg">
          {processing
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing with AI — please wait...</>
            : <><Sparkles className="w-4 h-4 mr-2" /> Process {pendingCount} Document{pendingCount > 1 ? 's' : ''} with AI</>}
        </Button>
      )}
    </div>
  );
}
